import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Users, Calendar, CheckCircle, XCircle, Clock,
  ChevronLeft, ChevronRight, Check, X, Edit2, Trash2, Plus,
  RefreshCw, DollarSign, Bell, Settings, AlertCircle,
  Download, FileText, Lock, Unlock, BarChart2, TrendingUp
} from 'lucide-react';

/* ─── Tokens ─── */
const T = {
  bg:'#f8f7ff', surface:'#ffffff', border:'#e8e4f3', borderL:'#f0edfb',
  purple:'#7c3aed', purpleL:'#8b5cf6', purpleBg:'#ede9fe', purpleText:'#6d28d9',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0', textXs:'#b8b3cc',
  green:'#059669', greenBg:'#d1fae5',
  red:'#dc2626', redBg:'#fee2e2',
  yellow:'#d97706', yellowBg:'#fef3c7',
  blue:'#2563eb', blueBg:'#dbeafe',
  pink:'#db2777', pinkBg:'#fce7f3',
  font:"'Poppins', sans-serif",
  shadow:'0 1px 4px rgba(124,58,237,0.06)',
  shadowMd:'0 4px 16px rgba(124,58,237,0.1)',
};

const MONTHS=['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmt = n => String(n).padStart(2,'0');
const fmtTime = iso => {
  if(!iso) return '—';
  const d=new Date(iso), h=d.getHours(), m=d.getMinutes();
  return `${fmt(h>12?h-12:h||12)}:${fmt(m)} ${h>=12?'PM':'AM'}`;
};
const fmtDur = mins => { if(!mins) return '—'; return `${Math.floor(mins/60)}h ${fmt(mins%60)}m`; };
const fmtINR = n => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;

/* ─── Shared components ─── */
const Spinner = () => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:48}}>
    <div style={{width:28,height:28,border:'3px solid #ede9fe',borderTop:`3px solid ${T.purple}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
  </div>
);

const Badge = ({status}) => {
  const m = {
    present:{bg:T.greenBg,c:T.green,l:'Present'},
    half_day:{bg:'#ccfbf1',c:'#0d9488',l:'Half Day'},
    absent:{bg:T.redBg,c:T.red,l:'Absent'},
    upcoming:{bg:'#f8f9fa',c:'#adb5bd',l:'Upcoming'},
    leave:{bg:T.purpleBg,c:T.purple,l:'On Leave'},
    permission:{bg:T.pinkBg,c:T.pink,l:'Permission'},
    holiday:{bg:T.yellowBg,c:T.yellow,l:'Holiday'},
    weekend:{bg:'#f3f4f6',c:'#6b7280',l:'Weekend'},
    regularized:{bg:T.blueBg,c:T.blue,l:'Regularized'},
    pending:{bg:T.yellowBg,c:T.yellow,l:'Pending'},
    approved:{bg:T.greenBg,c:T.green,l:'Approved'},
    rejected:{bg:T.redBg,c:T.red,l:'Rejected'},
    draft:{bg:'#f3f4f6',c:'#6b7280',l:'Draft'},
    paid:{bg:T.greenBg,c:T.green,l:'Paid'},
  }[status]||{bg:'#f3f4f6',c:'#6b7280',l:status||'—'};
  return <span style={{background:m.bg,color:m.c,fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20,whiteSpace:'nowrap'}}>{m.l}</span>;
};

const Btn = ({children,onClick,variant='primary',small,disabled,style={}}) => {
  const v={primary:{bg:T.purple,c:'#fff',b:'none'},secondary:{bg:'#fff',c:T.textMd,b:`1.5px solid ${T.border}`},green:{bg:T.green,c:'#fff',b:'none'},red:{bg:T.red,c:'#fff',b:'none'},yellow:{bg:'#d97706',c:'#fff',b:'none'}}[variant];
  return <button onClick={onClick} disabled={disabled} style={{display:'flex',alignItems:'center',gap:5,background:v.bg,color:v.c,border:v.b,borderRadius:9,padding:small?'6px 12px':'9px 16px',fontSize:small?12:13,fontWeight:600,cursor:disabled?'not-allowed':'pointer',fontFamily:T.font,opacity:disabled?0.6:1,whiteSpace:'nowrap',transition:'all 0.15s',...style}}>{children}</button>;
};

const Field = ({label,children,mb=14}) => (
  <div style={{marginBottom:mb}}>
    {label&&<label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:6}}>{label}</label>}
    {children}
  </div>
);

const inputSt = {width:'100%',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'10px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#faf9ff',outline:'none',boxSizing:'border-box'};

const Modal = ({title,subtitle,onClose,children,actions,wide}) => (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000,backdropFilter:'blur(4px)',fontFamily:T.font,padding:20}}>
    <div style={{background:'#fff',border:`1px solid ${T.border}`,borderRadius:16,padding:'24px 26px',width:'100%',maxWidth:wide?680:460,boxShadow:T.shadowMd,maxHeight:'90vh',overflow:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
        <div>
          <div style={{fontWeight:700,fontSize:17,color:T.text}}>{title}</div>
          {subtitle&&<div style={{fontWeight:400,fontSize:13,color:T.textSm,marginTop:3}}>{subtitle}</div>}
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,padding:2}}><X size={17}/></button>
      </div>
      {children}
      {actions&&<div style={{display:'flex',justifyContent:'flex-end',gap:9,marginTop:18}}>{actions}</div>}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════
   SECTION 1 — TODAY OVERVIEW
══════════════════════════════════════════════════ */
function TodaySection() {
  const todayStr = new Date().toISOString().split('T')[0];
  const yesterdayStr = (() => { const d=new Date(); d.setDate(d.getDate()-1); return d.toISOString().split('T')[0]; })();

  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [viewDate,setViewDate]=useState(todayStr); // today by default
  const [customDate,setCustomDate]=useState('');
  const [showCustom,setShowCustom]=useState(false);

  const load=useCallback(async(date)=>{
    setLoading(true);
    const d=date||viewDate;
    try{
      // Use date param if not today
      const params = d!==todayStr ? {date:d} : {};
      const r=await api.get('/attendance/admin/today',{params});
      setData(r.data);
    }
    catch{toast.error('Failed to load attendance overview');}
    finally{setLoading(false);}
  },[viewDate]);

  useEffect(()=>{load();},[load]);

  const switchDate=(d)=>{ setViewDate(d); setShowCustom(false); load(d); };
  const applyCustom=()=>{ if(customDate){ switchDate(customDate); } };

  if(loading) return <Spinner/>;
  const {overview=[],counts={}}=data||{};

  const displayDate = new Date(viewDate+'T00:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <h2 style={{fontWeight:800,fontSize:22,color:T.text,marginBottom:3}}>{viewDate===todayStr?"Today's Overview":viewDate===yesterdayStr?"Yesterday's Overview":"Attendance Overview"}</h2>
          <p style={{fontWeight:400,fontSize:14,color:T.textSm}}>{displayDate}</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {/* Quick date buttons */}
          <div style={{display:'flex',gap:2,background:T.borderL,borderRadius:9,padding:3}}>
            {[{l:'Today',d:todayStr},{l:'Yesterday',d:yesterdayStr}].map(({l,d})=>(
              <button key={d} onClick={()=>switchDate(d)}
                style={{background:viewDate===d?T.purple:'transparent',color:viewDate===d?'#fff':T.textSm,border:'none',borderRadius:7,padding:'5px 11px',fontSize:11,fontWeight:viewDate===d?600:400,cursor:'pointer',fontFamily:T.font,transition:'all 0.15s'}}>
                {l}
              </button>
            ))}
            <button onClick={()=>setShowCustom(s=>!s)}
              style={{background:showCustom||(!([todayStr,yesterdayStr].includes(viewDate)))?T.purple:'transparent',color:showCustom||(!([todayStr,yesterdayStr].includes(viewDate)))?'#fff':T.textSm,border:'none',borderRadius:7,padding:'5px 11px',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:T.font}}>
              📅 Pick Date
            </button>
          </div>
          {/* Custom date picker */}
          {showCustom&&(
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <input type="date" value={customDate} onChange={e=>setCustomDate(e.target.value)} max={todayStr}
                style={{border:`1.5px solid ${T.border}`,borderRadius:8,padding:'6px 10px',fontSize:12,fontFamily:T.font,color:T.text,background:'#fff',outline:'none'}}/>
              <Btn small onClick={applyCustom} disabled={!customDate}><Check size={12}/>Go</Btn>
            </div>
          )}
          <Btn variant="secondary" onClick={()=>load(viewDate)} small><RefreshCw size={13}/>Refresh</Btn>
        </div>
      </div>

      {/* Count cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:14,marginBottom:26}}>
        {[
          {l:'Present',v:counts.present||0,bg:T.greenBg,c:T.green,I:CheckCircle},
          {l:'Absent',v:counts.absent||0,bg:T.redBg,c:T.red,I:XCircle},
          {l:'On Leave',v:counts.onLeave||0,bg:T.purpleBg,c:T.purple,I:Calendar},
          {l:'Permission',v:counts.onPermission||0,bg:T.pinkBg,c:T.pink,I:Clock},
          {l:'Holiday',v:counts.holiday||0,bg:T.yellowBg,c:T.yellow,I:Calendar},
        ].map(({l,v,bg,c,I})=>(
          <div key={l} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'16px 18px',boxShadow:T.shadow}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:38,height:38,borderRadius:10,background:bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <I size={18} color={c}/>
              </div>
              <div>
                <div style={{fontWeight:800,fontSize:24,color:T.text,lineHeight:1}}>{v}</div>
                <div style={{fontWeight:500,fontSize:12,color:T.textSm,marginTop:3}}>{l}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Staff table */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 130px 130px 100px',padding:'11px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
          {['Employee','Login Time','Logout Time','Status'].map(h=>(
            <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.8}}>{h}</span>
          ))}
        </div>
        {overview.length===0?(
          <div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No employee records found</div>
        ):overview.map((emp,idx)=>(
          <div key={emp.id} style={{display:'grid',gridTemplateColumns:'1fr 130px 130px 100px',padding:'13px 20px',borderBottom:idx<overview.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:34,height:34,borderRadius:9,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:13,color:T.purpleText,flexShrink:0}}>
                {emp.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight:600,fontSize:14,color:T.text}}>{emp.name}</div>
                <div style={{fontWeight:400,fontSize:12,color:T.textSm,textTransform:'capitalize'}}>{emp.role}</div>
              </div>
            </div>
            <span style={{fontWeight:400,fontSize:14,color:T.textMd}}>{fmtTime(emp.attendance?.login_time)}</span>
            <span style={{fontWeight:400,fontSize:14,color:T.textMd}}>{fmtTime(emp.attendance?.logout_time)}</span>
            <Badge status={emp.attendance?.status||'absent'}/>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SECTION 2 — ALL STAFF ATTENDANCE TABLE
══════════════════════════════════════════════════ */
function AllStaffSection({users}) {
  const [month,setMonth]=useState(new Date().getMonth()+1);
  const [year,setYear]=useState(new Date().getFullYear());
  const [filterUser,setFilterUser]=useState('');
  const [filterStatus,setFilterStatus]=useState('');
  const [records,setRecords]=useState([]);
  const [loading,setLoading]=useState(false);
  const [editModal,setEditModal]=useState(null);
  const [editData,setEditData]=useState({});

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const params={month,year};
      if(filterUser) params.user_id=filterUser;
      const r=await api.get('/attendance/admin/monthly',{params});
      setRecords(r.data);
    }catch{toast.error('Failed to load');}
    finally{setLoading(false);}
  },[month,year,filterUser]);

  useEffect(()=>{load();},[load]);

  // Generate ALL days of the month — show every day even if no DB record
const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
const totalDays = getDaysInMonth(year, month);
const allDays = [];

// Get all unique users from records
const usersInRecords = [];
const seenUsers = {};
records.forEach(r => {
  if (r.user && !seenUsers[r.user_id]) {
    seenUsers[r.user_id] = true;
    usersInRecords.push({ id: r.user_id, name: r.user?.name, role: r.user?.role });
  }
});

// If filtering by specific user
const targetUsers = filterUser
  ? usersInRecords.filter(u => u.id === filterUser)
  : usersInRecords;

targetUsers.forEach(user => {
  for (let d = totalDays; d >= 1; d--) {
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const existing = records.find(r => r.user_id === user.id && r.date === dateStr);
    if (existing) {
      if (!filterStatus || existing.status === filterStatus) {
        allDays.push(existing);
      }
    } else {
      // No record — determine what it should be
      const dayOfWeek = new Date(dateStr + 'T00:00:00').getDay();
      const isSunday = dayOfWeek === 0;
      const today = new Date().toISOString().split('T')[0];
      const isFuture = dateStr > today;
      const syntheticStatus = isSunday ? 'weekend' : isFuture ? 'upcoming' : 'absent';
      if (!filterStatus || filterStatus === syntheticStatus) {
        allDays.push({
          id: `synthetic-${user.id}-${dateStr}`,
          user_id: user.id,
          user: { name: user.name, role: user.role },
          date: dateStr,
          status: syntheticStatus,
          login_time: null,
          logout_time: null,
          duration_minutes: null,
          _synthetic: true,
        });
      }
    }
  }
});

const filtered = allDays;

  const saveEdit=async()=>{
    try{
      await api.put(`/attendance/admin/override/${editModal.id}`,{...editData,existing_login:editModal.login_time});
      toast.success('Record updated');setEditModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>All Staff Attendance</h2>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {/* Month nav */}
          <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
            <button onClick={()=>{if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={15}/></button>
            <span style={{fontWeight:600,fontSize:13,color:T.text,minWidth:105,textAlign:'center'}}>{MONTHS[month-1]} {year}</span>
            <button onClick={()=>{if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={15}/></button>
          </div>
          <select value={filterUser} onChange={e=>setFilterUser(e.target.value)} style={{border:`1.5px solid ${T.border}`,borderRadius:9,padding:'7px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#fff',outline:'none',minWidth:150}}>
            <option value="">All Employees</option>
            {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{border:`1.5px solid ${T.border}`,borderRadius:9,padding:'7px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#fff',outline:'none'}}>
            <option value="">All Status</option>
            {['present','absent','leave','permission','holiday','weekend','regularized'].map(s=>(
              <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
            ))}
            <option value="half_day">Half Day</option>
          </select>
        </div>
      </div>

      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
        <div style={{display:'grid',gridTemplateColumns:'110px 1fr 110px 110px 90px 100px 70px',padding:'11px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
          {['Date','Employee','Login','Logout','Duration','Status',''].map(h=>(
            <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>
          ))}
        </div>
        {loading?<Spinner/>:filtered.length===0?(
          <div style={{padding:36,textAlign:'center',color:T.textSm,fontSize:14}}>No records found for selected filters</div>
        ):(
          <div style={{maxHeight:520,overflow:'auto'}}>
            {filtered.map((rec,idx)=>(
              <div key={rec.id} style={{display:'grid',gridTemplateColumns:'110px 1fr 110px 110px 90px 100px 70px',padding:'12px 20px',borderBottom:idx<filtered.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                <span style={{fontWeight:500,fontSize:13,color:T.textMd}}>{rec.date}</span>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:28,height:28,borderRadius:7,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:11,color:T.purpleText,flexShrink:0}}>
                    {rec.user?.name?.[0]?.toUpperCase()}
                  </div>
                  <span style={{fontWeight:500,fontSize:13,color:T.text}}>{rec.user?.name}</span>
                </div>
                <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{fmtTime(rec.login_time)}</span>
                <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{fmtTime(rec.logout_time)}</span>
                <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{fmtDur(rec.duration_minutes)}</span>
                {rec.status==='present'&&rec.is_half_day
                  ? <span style={{background:'#ccfbf1',color:'#0d9488',fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20}}>Half Day</span>
                  : <Badge status={rec.status}/>
                }
                {!rec._synthetic&&(
  <button onClick={()=>{setEditModal(rec);setEditData({status:rec.status,login_time:rec.login_time?.slice(0,16)||'',logout_time:rec.logout_time?.slice(0,16)||'',notes:rec.notes||'',existing_login:rec.login_time});}}
    style={{background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:7,padding:'5px 8px',display:'flex',alignItems:'center',gap:3,fontSize:11,fontWeight:600,fontFamily:T.font}}>
    <Edit2 size={11}/>Edit
  </button>
)}
              </div>
            ))}
          </div>
        )}
      </div>

      {editModal&&(
        <Modal title="Edit Attendance Record" subtitle={`${editModal.user?.name} — ${editModal.date}`} onClose={()=>setEditModal(null)}
          actions={[<Btn key="c" variant="secondary" onClick={()=>setEditModal(null)}>Cancel</Btn>,<Btn key="s" onClick={saveEdit}>Save Changes</Btn>]}>
          <Field label="Status">
            <select value={editData.status} onChange={e=>setEditData(p=>({...p,status:e.target.value}))} style={inputSt}>
              {['present','absent','leave','permission','holiday','regularized'].map(s=>(
                <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
              ))}
            </select>
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Login Time"><input type="datetime-local" value={editData.login_time} onChange={e=>setEditData(p=>({...p,login_time:e.target.value}))} style={inputSt}/></Field>
            <Field label="Logout Time"><input type="datetime-local" value={editData.logout_time} onChange={e=>setEditData(p=>({...p,logout_time:e.target.value}))} style={inputSt}/></Field>
          </div>
          <Field label="Admin Note (reason for override)">
            <textarea value={editData.notes} onChange={e=>setEditData(p=>({...p,notes:e.target.value}))} placeholder="e.g. Device issue, manual correction..." rows={3} style={{...inputSt,resize:'vertical',lineHeight:1.6}}/>
          </Field>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SECTION 3 — LEAVE & PERMISSION APPROVALS
══════════════════════════════════════════════════ */
function ApprovalsSection() {
  const [activeTab,setActiveTab]=useState('leaves');
  const [leaves,setLeaves]=useState([]);
  const [permissions,setPermissions]=useState([]);
  const [regularizations,setRegularizations]=useState([]);
  const [pendingCount,setPendingCount]=useState({leaves:0,permissions:0,regularizations:0});
  const [loading,setLoading]=useState(false);
  const [actionModal,setActionModal]=useState(null);
  const [adminNote,setAdminNote]=useState('');
  const [filterStatus,setFilterStatus]=useState('pending');

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const p=filterStatus?{status:filterStatus}:{};
      const [lr,pr,rr,pc]=await Promise.all([
        api.get('/leaves/admin/leaves',{params:p}),
        api.get('/leaves/admin/permissions',{params:p}),
        api.get('/leaves/admin/regularizations',{params:p}),
        api.get('/leaves/admin/pending-count'),
      ]);
      setLeaves(lr.data);setPermissions(pr.data);setRegularizations(rr.data);setPendingCount(pc.data);
    }catch{toast.error('Failed to load');}
    finally{setLoading(false);}
  },[filterStatus]);

  useEffect(()=>{load();},[load]);

  const doAction=async()=>{
    const {item,type,action}=actionModal;
    try{
      if(type==='leave') await api.put(`/leaves/admin/leaves/${item.id}`,{status:action,admin_note:adminNote});
      else if(type==='permission') await api.put(`/leaves/admin/permissions/${item.id}`,{status:action,admin_note:adminNote});
      else if(type==='regularization') await api.put(`/leaves/admin/regularizations/${item.id}`,{status:action,admin_note:adminNote});
      toast.success(action==='approved'?'Approved successfully':'Rejected');
      setActionModal(null);setAdminNote('');load();
    }catch(e){toast.error(e.response?.data?.error||'Action failed');}
  };

  const tabs=[
    {id:'leaves',label:'Leave Requests',count:pendingCount.leaves,data:leaves,color:T.purple},
    {id:'permissions',label:'Permissions',count:pendingCount.permissions,data:permissions,color:T.pink},
    {id:'regularizations',label:'Regularizations',count:pendingCount.regularizations,data:regularizations,color:T.blue},
  ];
  const currentData=tabs.find(t=>t.id===activeTab)?.data||[];

  const renderRow=(item,type,idx,arr)=>{
    const isLeave=type==='leave';
    const isPerm=type==='permission';
    return(
      <div key={item.id} style={{padding:'14px 20px',borderBottom:idx<arr.length-1?`1px solid ${T.borderL}`:'none'}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:10,flex:1}}>
            <div style={{width:36,height:36,borderRadius:9,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:13,color:T.purpleText,flexShrink:0}}>
              {item.user?.name?.[0]?.toUpperCase()}
            </div>
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3,flexWrap:'wrap'}}>
                <span style={{fontWeight:700,fontSize:14,color:T.text}}>{item.user?.name}</span>
                <Badge status={item.status}/>
                {isLeave&&<span style={{fontSize:11,fontWeight:600,color:T.purple,background:T.purpleBg,padding:'2px 8px',borderRadius:10,textTransform:'capitalize'}}>{item.leave_type}</span>}
                {isPerm&&<span style={{fontSize:11,fontWeight:600,color:T.pink,background:T.pinkBg,padding:'2px 8px',borderRadius:10}}>Half Day</span>}
              </div>
              <div style={{fontSize:13,color:T.textMd,marginBottom:2}}>
                {isLeave?`${item.from_date}${item.to_date!==item.from_date?` → ${item.to_date}`:''}`:(item.date||'')}
              </div>
              <div style={{fontSize:12,color:T.textSm}}>{item.reason}</div>
              {item.admin_note&&<div style={{fontSize:11,color:T.purple,marginTop:4,fontStyle:'italic'}}>Note: {item.admin_note}</div>}
            </div>
          </div>
          {item.status==='pending'&&(
            <div style={{display:'flex',gap:7,flexShrink:0}}>
              <button onClick={()=>setActionModal({item,type,action:'approved'})}
                style={{background:T.greenBg,border:`1px solid #6ee7b7`,color:T.green,cursor:'pointer',borderRadius:8,padding:'6px 13px',fontSize:12,fontWeight:700,fontFamily:T.font,display:'flex',alignItems:'center',gap:4}}>
                <Check size={12}/>Approve
              </button>
              <button onClick={()=>setActionModal({item,type,action:'rejected'})}
                style={{background:T.redBg,border:`1px solid #fca5a5`,color:T.red,cursor:'pointer',borderRadius:8,padding:'6px 13px',fontSize:12,fontWeight:700,fontFamily:T.font,display:'flex',alignItems:'center',gap:4}}>
                <X size={12}/>Reject
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>Leave & Permission Approvals</h2>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {pendingCount.total>0&&(
            <div style={{display:'flex',alignItems:'center',gap:6,background:T.redBg,border:`1px solid #fca5a5`,borderRadius:9,padding:'6px 12px'}}>
              <Bell size={13} color={T.red}/>
              <span style={{fontWeight:700,fontSize:12,color:T.red}}>{pendingCount.total} pending</span>
            </div>
          )}
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{border:`1.5px solid ${T.border}`,borderRadius:9,padding:'7px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#fff',outline:'none'}}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{display:'flex',alignItems:'center',gap:8,background:activeTab===t.id?T.purpleBg:T.surface,border:`1.5px solid ${activeTab===t.id?'#c4b5fd':T.border}`,borderRadius:10,padding:'9px 16px',fontSize:13,fontWeight:600,color:activeTab===t.id?T.purpleText:T.textMd,cursor:'pointer',fontFamily:T.font}}>
            {t.label}
            {t.count>0&&<span style={{background:T.red,color:'#fff',fontSize:10,fontWeight:700,minWidth:18,height:18,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 4px'}}>{t.count}</span>}
          </button>
        ))}
      </div>

      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
        {loading?<Spinner/>:currentData.length===0?(
          <div style={{padding:36,textAlign:'center',color:T.textSm,fontSize:14}}>No {filterStatus||''} requests found</div>
        ):(
          <div style={{maxHeight:520,overflow:'auto'}}>
            {activeTab==='leaves'&&leaves.map((item,idx,arr)=>renderRow(item,'leave',idx,arr))}
            {activeTab==='permissions'&&permissions.map((item,idx,arr)=>renderRow(item,'permission',idx,arr))}
            {activeTab==='regularizations'&&regularizations.map((item,idx,arr)=>renderRow(item,'regularization',idx,arr))}
          </div>
        )}
      </div>

      {/* Approve/Reject confirmation modal */}
      {actionModal&&(
        <Modal title={actionModal.action==='approved'?'Approve Request':'Reject Request'}
          subtitle={`${actionModal.item.user?.name} — ${actionModal.item.from_date||actionModal.item.date}`}
          onClose={()=>{setActionModal(null);setAdminNote('');}}>
          <div style={{background:actionModal.action==='approved'?T.greenBg:T.redBg,border:`1px solid ${actionModal.action==='approved'?'#6ee7b7':'#fca5a5'}`,borderRadius:10,padding:'11px 14px',marginBottom:16}}>
            <p style={{fontWeight:600,fontSize:13,color:actionModal.action==='approved'?T.green:T.red,margin:0}}>
              {actionModal.action==='approved'?'This will approve the request and update the attendance calendar.':'This will reject the request. Attendance will remain as marked.'}
            </p>
          </div>
          <Field label="Admin Note (optional)">
            <textarea value={adminNote} onChange={e=>setAdminNote(e.target.value)} placeholder={actionModal.action==='rejected'?'Reason for rejection...':'Any additional note...'} rows={3} style={{...inputSt,resize:'vertical',lineHeight:1.6}}/>
          </Field>
          <div style={{display:'flex',justifyContent:'flex-end',gap:9,marginTop:6}}>
            <Btn variant="secondary" onClick={()=>{setActionModal(null);setAdminNote('');}}>Cancel</Btn>
            <Btn variant={actionModal.action==='approved'?'green':'red'} onClick={doAction}>
              {actionModal.action==='approved'?<><Check size={13}/>Confirm Approve</>:<><X size={13}/>Confirm Reject</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SECTION 4 — HOLIDAY MANAGER
══════════════════════════════════════════════════ */
function HolidaySection() {
  const [holidays,setHolidays]=useState([]);
  const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null); // null | 'add' | holiday object
  const [form,setForm]=useState({date:'',name:'',is_recurring:false});
  const [year,setYear]=useState(new Date().getFullYear());

  const load=async()=>{
    setLoading(true);
    try{const r=await api.get('/attendance/holidays',{params:{year}});setHolidays(r.data);}
    catch{toast.error('Failed to load holidays');}
    finally{setLoading(false);}
  };
  useEffect(()=>{load();},[year]);

  const save=async()=>{
    if(!form.date||!form.name.trim()){toast.error('Date and name required');return;}
    try{
      if(modal==='add') await api.post('/attendance/holidays',form);
      else await api.put(`/attendance/holidays/${modal.id}`,form);
      toast.success(modal==='add'?'Holiday added':'Holiday updated');
      setModal(null);setForm({date:'',name:'',is_recurring:false});load();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const del=async(id,name)=>{
    if(!window.confirm(`Delete holiday "${name}"?`)) return;
    try{await api.delete(`/attendance/holidays/${id}`);toast.success('Deleted');load();}
    catch{toast.error('Delete failed');}
  };

  const openEdit=h=>{setModal(h);setForm({date:h.date,name:h.name,is_recurring:h.is_recurring});};

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>Holiday Manager</h2>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
            <button onClick={()=>setYear(y=>y-1)} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={15}/></button>
            <span style={{fontWeight:600,fontSize:13,color:T.text,minWidth:40,textAlign:'center'}}>{year}</span>
            <button onClick={()=>setYear(y=>y+1)} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={15}/></button>
          </div>
          <Btn onClick={()=>{setModal('add');setForm({date:'',name:'',is_recurring:false});}}>
            <Plus size={14}/>Add Holiday
          </Btn>
        </div>
      </div>

      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
        <div style={{display:'grid',gridTemplateColumns:'130px 1fr 110px 80px',padding:'11px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
          {['Date','Holiday Name','Recurring','Actions'].map(h=>(
            <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.8}}>{h}</span>
          ))}
        </div>
        {loading?<Spinner/>:holidays.length===0?(
          <div style={{padding:36,textAlign:'center',color:T.textSm,fontSize:14}}>No holidays for {year}</div>
        ):(
          holidays.map((h,idx)=>{
            const d=new Date(h.date+'T00:00:00');
            const dl=`${d.getDate()} ${MONTHS[d.getMonth()].slice(0,3)} ${d.getFullYear()}`;
            return(
              <div key={h.id} style={{display:'grid',gridTemplateColumns:'130px 1fr 110px 80px',padding:'13px 20px',borderBottom:idx<holidays.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:T.text}}>{dl}</div>
                  <div style={{fontWeight:400,fontSize:11,color:T.textXs,marginTop:1}}>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]}</div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:'#f59e0b',flexShrink:0}}/>
                  <span style={{fontWeight:500,fontSize:14,color:T.text}}>{h.name}</span>
                </div>
                <span style={{fontWeight:500,fontSize:13,color:h.is_recurring?T.green:T.textSm}}>{h.is_recurring?'Yes (Yearly)':'One-time'}</span>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={()=>openEdit(h)} style={{background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}}><Edit2 size={13}/></button>
                  <button onClick={()=>del(h.id,h.name)} style={{background:T.redBg,border:'none',color:T.red,cursor:'pointer',borderRadius:7,width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center'}}><Trash2 size={13}/></button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {modal&&(
        <Modal title={modal==='add'?'Add Holiday':'Edit Holiday'} onClose={()=>setModal(null)}
          actions={[<Btn key="c" variant="secondary" onClick={()=>setModal(null)}>Cancel</Btn>,<Btn key="s" onClick={save}>{modal==='add'?'Add Holiday':'Save Changes'}</Btn>]}>
          <Field label="Date"><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={inputSt}/></Field>
          <Field label="Holiday Name"><input type="text" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Pongal" style={inputSt}/></Field>
          <Field label="">
            <label style={{display:'flex',alignItems:'center',gap:9,cursor:'pointer'}}>
              <input type="checkbox" checked={form.is_recurring} onChange={e=>setForm(p=>({...p,is_recurring:e.target.checked}))} style={{width:16,height:16,accentColor:T.purple}}/>
              <span style={{fontWeight:500,fontSize:14,color:T.textMd}}>Recurring yearly (repeats every year on same date)</span>
            </label>
          </Field>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   EDIT SALARY SLIP MODAL
══════════════════════════════════════════════════ */
function EditSlipModal({slip, onClose, onSaved}) {
  const [mode, setMode] = useState('counts'); // 'counts' | 'attendance'
  const [saving, setSaving] = useState(false);

  // Mode 1: Direct day count edit
  const [form, setForm] = useState({
    present_days:    slip.present_days,
    absent_days:     slip.absent_days,
    leave_days:      slip.leave_days,
    permission_days: slip.permission_days,
    holiday_days:    slip.holiday_days,
  });

  // Mode 2: Edit one attendance day
  const [attDate, setAttDate] = useState('');
  const [attStatus, setAttStatus] = useState('present');
  const [attNote, setAttNote] = useState('');

  // Preview recalculated values for mode 1
  const previewLop = parseInt(form.absent_days) || 0;
  const previewDeduction = (previewLop * slip.daily_rate).toFixed(2);
  const previewNet = (slip.gross_salary - previewDeduction).toFixed(2);

  const monthStart = `${slip.year}-${String(slip.month).padStart(2,'0')}-01`;
  const daysInMonth = new Date(slip.year, slip.month, 0).getDate();
  const monthEnd   = `${slip.year}-${String(slip.month).padStart(2,'0')}-${daysInMonth}`;

  const save = async () => {
    setSaving(true);
    try {
      let body = {};
      if (mode === 'counts') {
        body = {
          present_days:    form.present_days,
          absent_days:     form.absent_days,
          leave_days:      form.leave_days,
          permission_days: form.permission_days,
          holiday_days:    form.holiday_days,
        };
      } else {
        if (!attDate) return toast.error('Select the date to edit');
        body = {
          attendance_date:   attDate,
          attendance_status: attStatus,
          edit_note:         attNote,
        };
      }
      await api.put(`/salary/admin/slips/${slip.id}/edit`, body);
      toast.success('Salary slip updated & recalculated');
      onSaved();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit Salary Slip" subtitle={`${slip.user?.name} — ${MONTHS[slip.month-1]} ${slip.year}`} onClose={onClose} wide
      actions={[
        <Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,
        <Btn key="s" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save & Recalculate'}</Btn>
      ]}>

      {/* Slip reset notice */}
      <div style={{background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:10,padding:'9px 14px',marginBottom:16}}>
        <p style={{fontSize:12,color:'#92400e',fontWeight:500,margin:0}}>
          ⚠ Editing will reset this slip to <strong>Draft</strong> status. You will need to re-approve after editing.
        </p>
      </div>

      {/* Mode toggle */}
      <div style={{display:'flex',gap:4,background:T.borderL,borderRadius:9,padding:3,marginBottom:18,width:'fit-content'}}>
        {[{id:'counts',l:'Edit Day Counts'},{id:'attendance',l:'Fix One Attendance Day'}].map(t=>(
          <button key={t.id} onClick={()=>setMode(t.id)}
            style={{background:mode===t.id?T.surface:'transparent',border:'none',borderRadius:7,padding:'6px 14px',fontSize:12,fontWeight:mode===t.id?700:500,color:mode===t.id?T.purple:T.textSm,cursor:'pointer',fontFamily:T.font,transition:'all 0.15s'}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* MODE 1: Edit counts */}
      {mode === 'counts' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16}}>
            {[
              {key:'present_days',   label:'Present Days',    color:T.green},
              {key:'absent_days',    label:'Absent / LOP',    color:T.red},
              {key:'leave_days',     label:'Approved Leave',  color:T.purple},
              {key:'permission_days',label:'Permission Days', color:'#db2777'},
              {key:'holiday_days',   label:'Holiday Days',    color:T.yellow},
            ].map(({key,label,color})=>(
              <div key={key}>
                <label style={{display:'block',fontWeight:600,fontSize:11,color:T.textMd,marginBottom:5}}>{label}</label>
                <input type="number" min="0" max={daysInMonth} value={form[key]}
                  onChange={e=>setForm(p=>({...p,[key]:e.target.value}))}
                  style={{...inputSt,fontSize:14,fontWeight:700,color,textAlign:'center'}}/>
              </div>
            ))}
          </div>

          {/* Preview */}
          <div style={{background:T.purpleBg,border:'1px solid #c4b5fd',borderRadius:10,padding:'12px 16px'}}>
            <div style={{fontWeight:700,fontSize:12,color:T.purpleText,marginBottom:8,textTransform:'uppercase',letterSpacing:0.8}}>Recalculated Preview</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
              <div style={{background:'#fff',borderRadius:7,padding:'8px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:T.textXs}}>LOP Days</div>
                <div style={{fontSize:18,fontWeight:800,color:T.red}}>{previewLop}</div>
              </div>
              <div style={{background:'#fff',borderRadius:7,padding:'8px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:T.textXs}}>Deduction</div>
                <div style={{fontSize:18,fontWeight:800,color:T.red}}>{fmtINR(previewDeduction)}</div>
              </div>
              <div style={{background:'#fff',borderRadius:7,padding:'8px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:T.textXs}}>Net Salary</div>
                <div style={{fontSize:18,fontWeight:800,color:T.green}}>{fmtINR(previewNet)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODE 2: Fix one attendance day */}
      {mode === 'attendance' && (
        <div>
          <div style={{background:'#dbeafe',border:'1px solid #93c5fd',borderRadius:10,padding:'9px 14px',marginBottom:14}}>
            <p style={{fontSize:12,color:'#1d4ed8',fontWeight:500,margin:0}}>
              📅 Change one day's attendance status. Salary will be automatically recalculated from the updated attendance records.
            </p>
          </div>
          <Field label={`Date (${MONTHS[slip.month-1]} ${slip.year} only)`}>
            <input type="date" value={attDate} min={monthStart} max={monthEnd}
              onChange={e=>setAttDate(e.target.value)} style={inputSt}/>
          </Field>
          <Field label="New Status for that Day">
            <select value={attStatus} onChange={e=>setAttStatus(e.target.value)} style={inputSt}>
              <option value="present">Present (worked full day)</option>
              <option value="absent">Absent (LOP — salary deducted)</option>
              <option value="leave">Approved Leave (no deduction)</option>
              <option value="permission">Permission / Half Day (no deduction)</option>
              <option value="holiday">Government Holiday (no deduction)</option>
              <option value="weekend">Weekend / Sunday (no deduction)</option>
            </select>
          </Field>
          <Field label="Reason / Note (optional)">
            <input style={inputSt} value={attNote} onChange={e=>setAttNote(e.target.value)}
              placeholder="e.g. Admin corrected from absent to present — device issue"/>
          </Field>

          {/* Status impact info */}
          <div style={{background:'#f0fdf4',border:'1px solid #6ee7b7',borderRadius:9,padding:'9px 14px',fontSize:12,color:'#065f46',fontWeight:500}}>
            ✓ <strong>Present, Leave, Permission, Holiday, Weekend</strong> → No LOP deduction<br/>
            ✗ <strong>Absent</strong> → LOP deduction ({fmtINR(slip.daily_rate)}/day will be deducted)
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════
   SECTION 5 — SALARY MANAGEMENT
══════════════════════════════════════════════════ */
function SalarySection({users}) {
  const [activeTab,setActiveTab]=useState('setup');
  const [salaries,setSalaries]=useState([]);
  const [slips,setSlips]=useState([]);
  const [loading,setLoading]=useState(false);
  const [calcLoading,setCalcLoading]=useState(false);
  const [month,setMonth]=useState(new Date().getMonth()+1);
  const [year,setYear]=useState(new Date().getFullYear());
  const [salaryModal,setSalaryModal]=useState(null);
  const [salaryForm,setSalaryForm]=useState({user_id:'',monthly_salary:'',joining_date:'',relieving_date:''});
  const [slipModal,setSlipModal]=useState(null);
const [editSlipModal,setEditSlipModal]=useState(null);
  const [filterStatus,setFilterStatus]=useState('');

  const loadSalaries=async()=>{
    setLoading(true);
    try{const r=await api.get('/salary/admin/salaries');setSalaries(r.data);}
    catch{toast.error('Failed to load salaries');}
    finally{setLoading(false);}
  };

const loadSlips=useCallback(async()=>{
  setLoading(true);
  try{
    const params={month,year};
    if(filterStatus) params.status=filterStatus;
    const [slipsRes, salariesRes] = await Promise.all([
      api.get('/salary/admin/slips',{params}),
      api.get('/salary/admin/salaries'),
    ]);
    const currentSalaries = salariesRes.data || [];
    const enriched = (slipsRes.data || []).map(slip => {
      const current = currentSalaries.find(s => s.user_id === slip.user_id);
      // Flag if stored slip salary is different from current employee salary
      const salaryMismatch = current &&
        parseFloat(current.monthly_salary) !== parseFloat(slip.monthly_salary);
      return { ...slip, salary_mismatch: salaryMismatch };
    });
    setSlips(enriched);
  }catch{toast.error('Failed to load slips');}
  finally{setLoading(false);}
},[month,year,filterStatus]);

  useEffect(()=>{if(activeTab==='setup')loadSalaries();else loadSlips();},[activeTab,loadSlips]);

  const saveSalary=async()=>{
    if(!salaryForm.user_id||!salaryForm.monthly_salary||!salaryForm.joining_date){toast.error('All fields required');return;}
    try{
      await api.post('/salary/admin/salaries',salaryForm);
      // If relieving date entered — call relieve API
      if(salaryForm.relieving_date&&salaryForm.user_id){
        await api.put(`/auth/users/${salaryForm.user_id}/relieve`,{relieving_date:salaryForm.relieving_date}).catch(()=>{});
      }
      toast.success('Salary saved');setSalaryModal(null);setSalaryForm({user_id:'',monthly_salary:'',joining_date:'',relieving_date:''});loadSalaries();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const calculate=async()=>{
    if(!window.confirm(`Calculate salary for all employees for ${MONTHS[month-1]} ${year}?`)) return;
    setCalcLoading(true);
    try{
      const r=await api.post(`/salary/admin/calculate/${month}/${year}`);
      toast.success(`Salary calculated for ${r.data.results?.length||0} employees`);
      loadSlips();setActiveTab('slips');
    }catch(e){toast.error(e.response?.data?.error||'Calculation failed');}
    finally{setCalcLoading(false);}
  };

  const approveSlip=async(id)=>{
    try{await api.put(`/salary/admin/slips/${id}/approve`);toast.success('Approved & employee notified');loadSlips();}
    catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const markPaid=async(id)=>{
    try{await api.put(`/salary/admin/slips/${id}/paid`);toast.success('Marked as paid');loadSlips();}
    catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>Salary Management</h2>
        <div style={{display:'flex',gap:10}}>
          {[{id:'setup',l:'Salary Setup'},{id:'slips',l:'Salary Slips'}].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{background:activeTab===t.id?T.purpleBg:T.surface,border:`1.5px solid ${activeTab===t.id?'#c4b5fd':T.border}`,borderRadius:9,padding:'8px 16px',fontSize:13,fontWeight:600,color:activeTab===t.id?T.purpleText:T.textMd,cursor:'pointer',fontFamily:T.font}}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* SALARY SETUP */}
      {activeTab==='setup'&&(
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <Btn onClick={()=>{setSalaryModal(true);setSalaryForm({user_id:'',monthly_salary:'',joining_date:'',relieving_date:''});}}>
              <Plus size={14}/>Set Employee Salary
            </Btn>
          </div>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 140px 120px 120px 90px 80px',padding:'11px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
              {['Employee','Monthly Salary','Joining Date','Relieving Date','Daily Rate',''].map(h=>(
                <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>
              ))}
            </div>
            {loading?<Spinner/>:salaries.length===0?(
              <div style={{padding:36,textAlign:'center',color:T.textSm,fontSize:14}}>No salary data. Add employee salaries to get started.</div>
            ):(
              salaries.map((s,idx)=>{
                // Get days in that display month (use current month as reference for setup)
const daysThisMonth = new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).getDate();
const dailyRate = (s.monthly_salary / daysThisMonth).toFixed(2);
                return(
                  <div key={s.id} style={{display:'grid',gridTemplateColumns:'1fr 140px 120px 120px 90px 80px',padding:'13px 20px',borderBottom:idx<salaries.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:34,height:34,borderRadius:9,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:13,color:T.purpleText}}>
                        {s.user?.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:600,fontSize:14,color:T.text}}>{s.user?.name}</div>
                        <div style={{fontWeight:400,fontSize:12,color:T.textSm,textTransform:'capitalize'}}>{s.user?.role}</div>
                      </div>
                    </div>
                    <span style={{fontWeight:700,fontSize:14,color:T.green}}>{fmtINR(s.monthly_salary)}</span>
                    <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{s.joining_date}</span>
                    <span style={{fontWeight:400,fontSize:13,color:s.relieving_date?T.red:T.textXs}}>{s.relieving_date||'—'}</span>
                    <span style={{fontWeight:500,fontSize:13,color:T.textMd}}>₹{dailyRate}/day</span>
                    <button onClick={()=>{setSalaryModal(true);setSalaryForm({user_id:s.user_id,monthly_salary:s.monthly_salary,joining_date:s.joining_date,relieving_date:s.relieving_date||''});}}
                      style={{background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:7,padding:'5px 8px',display:'flex',alignItems:'center',gap:3,fontSize:11,fontWeight:600,fontFamily:T.font}}>
                      <Edit2 size={11}/>Edit
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* SALARY SLIPS */}
      {activeTab==='slips'&&(
        <div>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
              <button onClick={()=>{if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={15}/></button>
              <span style={{fontWeight:600,fontSize:13,color:T.text,minWidth:105,textAlign:'center'}}>{MONTHS[month-1]} {year}</span>
              <button onClick={()=>{if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={15}/></button>
            </div>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{border:`1.5px solid ${T.border}`,borderRadius:9,padding:'7px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#fff',outline:'none'}}>
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
            </select>
            {slips.some(s=>s.salary_mismatch)&&(
  <div style={{display:'flex',alignItems:'center',gap:8,background:'#fee2e2',
    border:'1px solid #fca5a5',borderRadius:9,padding:'7px 14px'}}>
    <span style={{fontSize:12,fontWeight:600,color:T.red}}>
      ⚠ Salary was updated — click Calculate Salary to apply new amounts
    </span>
  </div>
)}
<Btn onClick={calculate} disabled={calcLoading}>
  <RefreshCw size={14}/>{calcLoading?'Calculating...':'Calculate Salary'}
</Btn>
          </div>

          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 100px 100px 100px 90px 90px 120px',padding:'11px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
              {['Employee','Gross','Deduction','Net Salary','Present','LOP','Actions'].map(h=>(
                <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>
              ))}
            </div>
            {loading?<Spinner/>:slips.length===0?(
              <div style={{padding:36,textAlign:'center',color:T.textSm,fontSize:14}}>No salary slips for {MONTHS[month-1]} {year}. Click "Calculate Salary" to generate.</div>
            ):(
              slips.map((slip,idx)=>(
                <div key={slip.id} style={{display:'grid',gridTemplateColumns:'1fr 100px 100px 100px 90px 90px 120px',padding:'13px 20px',borderBottom:idx<slips.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:9}}>
                    <div style={{width:32,height:32,borderRadius:8,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,color:T.purpleText}}>
                      {slip.user?.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontWeight:600,fontSize:13,color:T.text}}>{slip.user?.name}</div>
{slip.is_prorated&&<div style={{fontSize:10,color:T.yellow,fontWeight:600}}>Pro-rated</div>}
{slip.salary_mismatch&&(
  <div style={{fontSize:9,color:T.red,fontWeight:700,background:T.redBg,
    padding:'2px 7px',borderRadius:8,marginTop:2,display:'inline-block'}}>
    ⚠ Salary changed — recalculate
  </div>
)}
                    </div>
                  </div>
                  <span style={{fontWeight:500,fontSize:13,color:T.textMd}}>{fmtINR(slip.gross_salary)}</span>
                  <span style={{fontWeight:500,fontSize:13,color:slip.lop_deduction>0?T.red:T.textMd}}>{fmtINR(slip.lop_deduction)}</span>
                  <span style={{fontWeight:700,fontSize:13,color:T.green}}>{fmtINR(slip.net_salary)}</span>
                  <span style={{fontWeight:500,fontSize:13,color:T.textMd}}>{slip.present_days}d</span>
                  <span style={{fontWeight:500,fontSize:13,color:slip.lop_days>0?T.red:T.textMd}}>{slip.lop_days}d</span>
                  <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                    <button onClick={()=>setSlipModal(slip)} style={{background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:7,padding:'4px 8px',fontSize:11,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}>
  <FileText size={11}/>View
</button>
<button onClick={()=>setEditSlipModal(slip)} style={{background:'#fef3c7',border:'1px solid #fcd34d',color:'#92400e',cursor:'pointer',borderRadius:7,padding:'4px 8px',fontSize:11,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}>
  <Edit2 size={11}/>Edit
</button>
                    {slip.status==='draft'&&(
                      <button onClick={()=>approveSlip(slip.id)} style={{background:T.greenBg,border:`1px solid #6ee7b7`,color:T.green,cursor:'pointer',borderRadius:7,padding:'4px 8px',fontSize:11,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}>
                        <Check size={10}/>Approve
                      </button>
                    )}
                    {slip.status==='approved'&&(
                      <button onClick={()=>markPaid(slip.id)} style={{background:'#d97706',border:'none',color:'#fff',cursor:'pointer',borderRadius:7,padding:'4px 8px',fontSize:11,fontWeight:600,fontFamily:T.font}}>
                        Paid
                      </button>
                    )}
                    {slip.status!=='draft'&&<Badge status={slip.status}/>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Salary Setup Modal */}
      {salaryModal&&(
        <Modal title="Set Employee Salary" subtitle="Basic salary only — no PF/ESI/tax" onClose={()=>setSalaryModal(null)}
          actions={[<Btn key="c" variant="secondary" onClick={()=>setSalaryModal(null)}>Cancel</Btn>,<Btn key="s" onClick={saveSalary}>Save Salary</Btn>]}>
          <Field label="Employee">
            <select value={salaryForm.user_id} onChange={e=>setSalaryForm(p=>({...p,user_id:e.target.value}))} style={inputSt}>
              <option value="">Select employee...</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Field>
          <Field label="Monthly Salary (₹)">
            <input type="number" value={salaryForm.monthly_salary} onChange={e=>setSalaryForm(p=>({...p,monthly_salary:e.target.value}))} placeholder="e.g. 25000" style={inputSt}/>
          </Field>
          <Field label="Joining Date">
            <input type="date" value={salaryForm.joining_date} onChange={e=>setSalaryForm(p=>({...p,joining_date:e.target.value}))} style={inputSt}/>
          </Field>
          <Field label="Relieving Date (leave blank if still employed)">
            <input type="date" value={salaryForm.relieving_date} onChange={e=>setSalaryForm(p=>({...p,relieving_date:e.target.value}))} style={inputSt}/>
            {salaryForm.relieving_date&&<p style={{fontWeight:500,fontSize:11,color:T.red,marginTop:4}}>⚠ Once saved — this employee will not be able to login after {salaryForm.relieving_date}. Their records are kept.</p>}
          </Field>
          <div style={{background:T.purpleBg,border:`1px solid #c4b5fd`,borderRadius:10,padding:'11px 14px',marginTop:4}}>
            <p style={{fontWeight:500,fontSize:12,color:T.purpleText,margin:0,lineHeight:1.6}}>
              💡 Salary is calculated as: All calendar days in month × Daily rate. Approved leaves and permissions are <strong>not deducted</strong>. Only absent (LOP) days are deducted.
            </p>
          </div>
        </Modal>
      )}

     {/* Slip View Modal */}
{slipModal&&<SlipViewModal slip={slipModal} onClose={()=>setSlipModal(null)}/>}

{/* Edit Slip Modal */}
{editSlipModal&&(
  <EditSlipModal
    slip={editSlipModal}
    onClose={()=>setEditSlipModal(null)}
    onSaved={()=>{setEditSlipModal(null);loadSlips();}}
  />
)}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   SALARY SLIP VIEW
══════════════════════════════════════════════════ */
function SlipViewModal({slip,onClose}) {
  const [detail,setDetail]=useState(null);
  useEffect(()=>{
    api.get(`/salary/slip/${slip.id}/print`).then(r=>setDetail(r.data)).catch(()=>setDetail(slip));
  },[slip.id]);

  const s=detail||slip;
  const monthName=MONTHS[(s.month||1)-1];

  const printSlip=()=>{
    const w=window.open('','_blank');
    const empId='R360-'+String(s.user_id||'001').slice(-3).padStart(3,'0');
    const payPeriodStart=`01 ${monthName} ${s.year}`;
    const payPeriodEnd=`${s.total_days} ${monthName} ${s.year}`;
    w.document.write(`
      <!DOCTYPE html><html><head>
      <title>Salary Slip - ${s.user?.name} - ${monthName} ${s.year}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Poppins',Arial,sans-serif;color:#1a1a2e;background:#fff;padding:20px;}
        .slip{max-width:720px;margin:0 auto;border:1px solid #dde3f0;border-radius:4px;overflow:hidden;}

        /* TOP HEADER */
        .top-header{background:#fff;padding:28px 36px 18px;text-align:center;border-bottom:2px solid #1e3a6e;}
        .logo-img{height:72px;margin-bottom:10px;display:block;margin-left:auto;margin-right:auto;}
        .company-name{font-size:28px;font-weight:900;color:#1e3a6e;letter-spacing:1px;margin-bottom:4px;}
        .company-sub{font-size:13px;color:#555;font-weight:400;margin-bottom:3px;}
        .company-tagline{font-size:12px;color:#777;font-style:italic;margin-bottom:0;}

        /* SLIP TITLE BAR */
        .slip-title-bar{background:#1e3a6e;color:#fff;text-align:center;padding:10px;font-size:15px;font-weight:700;letter-spacing:2px;}

        /* EMP INFO */
        .emp-section{padding:18px 36px;border-bottom:1px solid #dde3f0;display:grid;grid-template-columns:1fr 1px 1fr;gap:0;}
        .emp-col{padding:0 16px;}
        .emp-col:first-child{padding-left:0;}
        .emp-divider{background:#dde3f0;width:1px;margin:0;}
        .emp-row{display:grid;grid-template-columns:130px 8px 1fr;align-items:baseline;padding:5px 0;font-size:13px;}
        .emp-label{color:#555;font-weight:500;}
        .emp-colon{color:#555;font-weight:500;text-align:center;}
        .emp-value{color:#1a1a2e;font-weight:700;}

        /* SECTION TITLE */
        .sec-title{font-size:12px;font-weight:700;color:#1e3a6e;text-transform:uppercase;letter-spacing:1.5px;margin:18px 36px 10px;}

        /* ATTENDANCE TABLE */
        .att-table{margin:0 36px 18px;border:1px solid #dde3f0;border-radius:4px;overflow:hidden;}
        .att-table-header{display:grid;grid-template-columns:1fr 1fr;background:#1e3a6e;color:#fff;font-size:12px;font-weight:600;padding:9px 16px;text-align:center;}
        .att-table-header span:first-child{text-align:left;}
        .att-row{display:grid;grid-template-columns:1fr 1fr;padding:8px 16px;font-size:13px;border-top:1px solid #f0f3f8;}
        .att-row:nth-child(even){background:#f8faff;}
        .att-row.total{background:#eef2fb;font-weight:700;border-top:2px solid #dde3f0;}
        .att-row span:last-child{text-align:center;font-weight:600;}

        /* EARNINGS & DEDUCTIONS */
        .earn-ded{display:grid;grid-template-columns:1fr 1fr;gap:0;margin:0 36px 18px;border:1px solid #dde3f0;border-radius:4px;overflow:hidden;}
        .earn-col{border-right:1px solid #dde3f0;}
        .earn-col:last-child{border-right:none;}
        .earn-col-title{background:#1e3a6e;color:#fff;font-size:12px;font-weight:700;padding:9px 16px;letter-spacing:1px;text-transform:uppercase;}
        .earn-header{display:grid;grid-template-columns:1fr 100px;padding:7px 16px;background:#f0f4ff;font-size:11px;font-weight:700;color:#1e3a6e;border-bottom:1px solid #dde3f0;}
        .earn-row{display:grid;grid-template-columns:1fr 100px;padding:8px 16px;font-size:13px;border-bottom:1px solid #f0f3f8;}
        .earn-row.total{background:#eef2fb;font-weight:700;border-top:2px solid #dde3f0;border-bottom:none;}
        .earn-row span:last-child{text-align:right;font-weight:600;}
        .earn-row.total span{font-weight:800;}

        /* NET SALARY */
        .net-box{margin:0 36px 24px;border:2px solid #dde3f0;border-radius:8px;display:flex;align-items:center;gap:18px;padding:16px 22px;background:#f8faff;}
        .net-icon{width:48px;height:48px;background:#1e3a6e;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;flex-shrink:0;}
        .net-label{font-size:15px;font-weight:700;color:#1a1a2e;text-transform:uppercase;letter-spacing:0.5px;}
        .net-words{font-size:11px;color:#777;margin-top:3px;}
        .net-amount{margin-left:auto;font-size:28px;font-weight:900;color:#1e3a6e;}

        /* SIGNATURE */
        .sig-section{text-align:center;padding:10px 36px 18px;border-top:1px solid #f0f3f8;}
        .sig-line{width:160px;border-top:1.5px solid #1e3a6e;margin:0 auto 6px;}
        .sig-label{font-size:11px;color:#777;margin-bottom:2px;}
        .sig-name{font-size:14px;font-weight:700;color:#1a1a2e;}
        .sig-title{font-size:11px;color:#555;}
        .sys-note{font-size:10px;color:#aaa;margin-top:10px;text-align:center;}

        /* CONTACT FOOTER */
        .contact-footer{display:grid;grid-template-columns:1fr 1fr 1fr;border-top:2px solid #1e3a6e;background:#f8faff;padding:14px 36px;}
        .contact-item{display:flex;align-items:center;gap:10px;justify-content:center;}
        .contact-icon{width:32px;height:32px;border-radius:50%;background:#1e3a6e;display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;flex-shrink:0;}
        .contact-text{font-size:11px;color:#777;line-height:1.5;}
        .contact-text strong{display:block;color:#1a1a2e;font-weight:600;font-size:11px;}

        @media print{body{padding:0;} .slip{border:none;max-width:100%;}}
      </style></head>
      <body>
      <div class="slip">

        <!-- TOP HEADER -->
        <div class="top-header">
          <img class="logo-img" src="https://reach360office.in/nova-logo.png" alt="Reach 360" onerror="this.style.display='none'"/>
          <div class="company-name">REACH 360</div>
          <div class="company-sub">Photography &amp; Media Production</div>
          <div class="company-tagline">"Lets grow together"</div>
        </div>

        <!-- SALARY SLIP TITLE BAR -->
        <div class="slip-title-bar">SALARY SLIP – ${monthName} ${s.year}</div>

        <!-- EMPLOYEE INFO -->
        <div class="emp-section">
          <div class="emp-col">
            <div class="emp-row"><span class="emp-label">Employee Name</span><span class="emp-colon">:</span><span class="emp-value">${s.user?.name||'—'}</span></div>
            <div class="emp-row"><span class="emp-label">Employee ID</span><span class="emp-colon">:</span><span class="emp-value">${empId}</span></div>
            <div class="emp-row"><span class="emp-label">Designation</span><span class="emp-colon">:</span><span class="emp-value" style="text-transform:capitalize">${s.user?.role||'—'}</span></div>
            <div class="emp-row"><span class="emp-label">Joining Date</span><span class="emp-colon">:</span><span class="emp-value">${s.joining_date?new Date(s.joining_date+'T00:00:00').toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}):'—'}</span></div>
          </div>
          <div class="emp-divider"></div>
          <div class="emp-col">
            <div class="emp-row"><span class="emp-label">Pay Period</span><span class="emp-colon">:</span><span class="emp-value">${payPeriodStart} – ${payPeriodEnd}</span></div>
            <div class="emp-row"><span class="emp-label">Pay Frequency</span><span class="emp-colon">:</span><span class="emp-value">Monthly</span></div>
            <div class="emp-row"><span class="emp-label">Days in Month</span><span class="emp-colon">:</span><span class="emp-value">${s.total_days}</span></div>
            <div class="emp-row"><span class="emp-label">Daily Rate</span><span class="emp-colon">:</span><span class="emp-value">${fmtINR(s.daily_rate)}/day</span></div>
          </div>
        </div>

        <!-- ATTENDANCE SUMMARY -->
        <div class="sec-title">Attendance Summary</div>
        <div class="att-table">
          <div class="att-table-header"><span>Particulars</span><span>Days</span></div>
          <div class="att-row"><span>Present</span><span>${s.present_days}</span></div>
          <div class="att-row"><span>Absent (LOP)</span><span>${s.absent_days}</span></div>
          <div class="att-row"><span>Approved Leave</span><span>${s.leave_days}</span></div>
          <div class="att-row"><span>Permission</span><span>${s.permission_days}</span></div>
          <div class="att-row"><span>Holidays</span><span>${s.holiday_days}</span></div>
          <div class="att-row total"><span>Total Days</span><span>${s.total_days}</span></div>
        </div>

        <!-- EARNINGS & DEDUCTIONS -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;margin:0 36px 18px;border:1px solid #dde3f0;border-radius:4px;overflow:hidden;">
          <!-- EARNINGS -->
          <div style="border-right:1px solid #dde3f0;">
            <div class="earn-col-title">Earnings</div>
            <div class="earn-header"><span>Component</span><span style="text-align:right;">Amount (₹)</span></div>
            <div class="earn-row"><span>Basic Salary</span><span>${Number(s.monthly_salary||0).toFixed(2)}</span></div>
            ${s.is_prorated?`<div class="earn-row" style="color:#d97706;font-size:11px;"><span>Pro-rated (${s.joining_date})</span><span>${Number(s.gross_salary||0).toFixed(2)}</span></div>`:''}
            <div class="earn-row total"><span>Total Earnings</span><span>${Number(s.gross_salary||0).toFixed(2)}</span></div>
          </div>
          <!-- DEDUCTIONS -->
          <div>
            <div class="earn-col-title">Deductions</div>
            <div class="earn-header"><span>Component</span><span style="text-align:right;">Amount (₹)</span></div>
            <div class="earn-row"><span>Loss of Pay (LOP)</span><span style="color:${s.lop_deduction>0?'#dc2626':'#1a1a2e'}">${Number(s.lop_deduction||0).toFixed(2)}</span></div>
            <div class="earn-row total"><span>Total Deductions</span><span style="color:${s.lop_deduction>0?'#dc2626':'#1a1a2e'}">${Number(s.lop_deduction||0).toFixed(2)}</span></div>
          </div>
        </div>

        <!-- NET SALARY -->
        <div class="net-box">
          <div class="net-icon">₹</div>
          <div>
            <div class="net-label">Net Salary Payable</div>
            <div class="net-words">(In Words: ${s.net_salary_words||''})</div>
          </div>
          <div class="net-amount">₹${Number(s.net_salary||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</div>
        </div>

        <!-- SIGNATURE -->
        <div class="sig-section">
          <div class="sig-line"></div>
          <div class="sig-label">Approved By</div>
          <div class="sig-name">Kailash</div>
          <div class="sig-title">Founder, Reach 360</div>
          <div class="sys-note">(This is a system-generated payslip. Signature not required.)</div>
        </div>

        <!-- CONTACT FOOTER -->
        <div class="contact-footer">
          <div class="contact-item">
            <div class="contact-icon">✉</div>
            <div class="contact-text"><strong>Email</strong>reach360d@gmail.com</div>
          </div>
          <div class="contact-item">
            <div class="contact-icon">🌐</div>
            <div class="contact-text"><strong>Website</strong>www.reach360.in</div>
          </div>
          <div class="contact-item">
            <div class="contact-icon">📞</div>
            <div class="contact-text"><strong>Contact</strong>9443394439</div>
          </div>
        </div>

      </div>
      </body></html>
    `);
    w.document.close();
    setTimeout(()=>w.print(),500);
  };

  return(
    <Modal title={`Salary Slip — ${monthName} ${s.year}`} subtitle={s.user?.name} onClose={onClose} wide
      actions={[
        <Btn key="print" variant="secondary" onClick={printSlip}><Download size={13}/>Print / Download PDF</Btn>,
        <Btn key="c" variant="secondary" onClick={onClose}>Close</Btn>
      ]}>
      {!detail?<Spinner/>:(
        <div style={{fontFamily:T.font}}>
          {/* Quick summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
            {[['Gross',fmtINR(s.gross_salary),T.text],['Deductions',fmtINR(s.lop_deduction),s.lop_deduction>0?T.red:T.textSm],['Net Salary',fmtINR(s.net_salary),T.green]].map(([l,v,c])=>(
              <div key={l} style={{background:'#faf9ff',border:`1px solid ${T.border}`,borderRadius:10,padding:'14px 16px',textAlign:'center'}}>
                <div style={{fontWeight:400,fontSize:12,color:T.textSm,marginBottom:5}}>{l}</div>
                <div style={{fontWeight:800,fontSize:18,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          {/* Attendance breakdown */}
          <div style={{background:T.purpleBg,border:`1px solid #c4b5fd`,borderRadius:10,padding:'14px 16px',marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:12,color:T.purpleText,marginBottom:10,textTransform:'uppercase',letterSpacing:0.8}}>Attendance Summary</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
              {[['Total Days',s.total_days],['Present',s.present_days],['Absent/LOP',s.absent_days],['Leave',s.leave_days],['Permission',s.permission_days],['Holidays',s.holiday_days]].map(([l,v])=>(
                <div key={l} style={{display:'flex',justifyContent:'space-between',background:'#fff',borderRadius:7,padding:'7px 10px'}}>
                  <span style={{fontSize:12,color:T.textMd,fontWeight:500}}>{l}</span>
                  <span style={{fontSize:13,color:T.text,fontWeight:700}}>{v}</span>
                </div>
              ))}
            </div>
          </div>
         {s.is_prorated&&(()=>{
  const jd = new Date((s.joining_date||'')+'T00:00:00');
  const me = new Date(s.year, s.month, 0);
  const effDays = Math.ceil((me - jd)/(1000*60*60*24)) + 1;
  return(
    <div style={{background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:9,padding:'9px 14px',marginBottom:12,fontSize:12,color:'#92400e',fontWeight:500}}>
      ⚡ Pro-rated salary: Employee joined on {s.joining_date} ({effDays} of {s.total_days} days counted)
    </div>
  );
})()}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:T.greenBg,border:`1px solid #6ee7b7`,borderRadius:10,padding:'14px 18px'}}>
            <span style={{fontWeight:700,fontSize:14,color:T.text}}>NET SALARY PAYABLE</span>
            <span style={{fontWeight:900,fontSize:22,color:T.green}}>{fmtINR(s.net_salary)}</span>
          </div>
          {s.net_salary_words&&<p style={{fontSize:11,color:T.textSm,textAlign:'center',marginTop:6,fontStyle:'italic'}}>{s.net_salary_words}</p>}
        </div>
      )}
    </Modal>
  );
}


/* ══════════════════════════════════════════════════
   SECTION 6 — REPORTS + MONTH SUMMARY + MONTH LOCK
══════════════════════════════════════════════════ */
function ReportsSection({users}) {
  const [month,setMonth]=useState(new Date().getMonth()+1);
  const [year,setYear]=useState(new Date().getFullYear());
  const [summary,setSummary]=useState([]);
  const [loading,setLoading]=useState(false);
  const [lockedMonths,setLockedMonths]=useState(()=>{
    try{return JSON.parse(localStorage.getItem('nova_locked_months')||'[]');}catch{return[];}
  });
  const [selectedUser,setSelectedUser]=useState('');
  const [userDetail,setUserDetail]=useState(null);
  const [detailLoading,setDetailLoading]=useState(false);

  const monthKey = `${year}-${String(month).padStart(2,'0')}`;
  const isLocked = lockedMonths.includes(monthKey);

  const loadSummary=useCallback(async()=>{
    setLoading(true);
    try{const r=await api.get('/attendance/admin/summary',{params:{month,year}});setSummary(r.data);}
    catch{toast.error('Failed to load summary');}
    finally{setLoading(false);}
  },[month,year]);

  useEffect(()=>{loadSummary();},[loadSummary]);

  const loadUserDetail=async(userId)=>{
    setSelectedUser(userId);
    setDetailLoading(true);
    try{
      const r=await api.get('/attendance/admin/monthly',{params:{month,year,user_id:userId}});
      setUserDetail(r.data);
    }catch{toast.error('Failed to load detail');}
    finally{setDetailLoading(false);}
  };

  const toggleLock=()=>{
    let updated;
    if(isLocked){
      if(!window.confirm(`Unlock ${MONTHS[month-1]} ${year}? This will allow attendance edits.`)) return;
      updated=lockedMonths.filter(m=>m!==monthKey);
      toast.success(`${MONTHS[month-1]} ${year} unlocked`);
    } else {
      if(!window.confirm(`Lock ${MONTHS[month-1]} ${year}? No attendance changes can be made after locking.`)) return;
      updated=[...lockedMonths,monthKey];
      toast.success(`${MONTHS[month-1]} ${year} locked`);
    }
    setLockedMonths(updated);
    localStorage.setItem('nova_locked_months',JSON.stringify(updated));
  };

  const exportCSV=()=>{
    if(!summary.length){toast('No data to export',{icon:'ℹ️'});return;}
    const rows=[
      ['Employee','Role','Present','Absent','Leave','Permission','Holiday','Total Working'],
      ...summary.map(s=>[s.name,s.role,s.present,s.absent,s.leave,s.permission,s.holiday,(s.present+s.absent+s.leave+s.permission+s.holiday)]),
    ];
    const csv=rows.map(r=>r.join(',')).join('\n');
    const blob=new Blob([csv],{type:'text/csv'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(blob);
    a.download=`attendance_${MONTHS[month-1]}_${year}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const fmtPct=(p,w)=>w>0?Math.round((p/w)*100):0;

  return(
    <div>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>Monthly Reports</h2>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
          {/* Month nav */}
          <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
            <button onClick={()=>{if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={15}/></button>
            <span style={{fontWeight:600,fontSize:13,color:T.text,minWidth:105,textAlign:'center'}}>{MONTHS[month-1]} {year}</span>
            <button onClick={()=>{if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={15}/></button>
          </div>

          {/* Lock/Unlock */}
          <button onClick={toggleLock}
            style={{display:'flex',alignItems:'center',gap:6,background:isLocked?T.redBg:T.greenBg,border:`1px solid ${isLocked?'#fca5a5':'#6ee7b7'}`,color:isLocked?T.red:T.green,borderRadius:9,padding:'7px 14px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:T.font}}>
            {isLocked?<><Lock size={13}/>Locked — Click to Unlock</>:<><Unlock size={13}/>Lock Month</>}
          </button>

          {/* CSV Export */}
          <Btn variant="secondary" onClick={exportCSV} small>
            <Download size={13}/>Export CSV
          </Btn>
        </div>
      </div>

      {isLocked&&(
        <div style={{background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:10,padding:'11px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:9}}>
          <Lock size={14} color={T.red}/>
          <span style={{fontWeight:600,fontSize:13,color:T.red}}>This month is locked — no attendance edits allowed. Unlock to make changes.</span>
        </div>
      )}

      {/* All-staff summary table */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow,marginBottom:24}}>
        <div style={{padding:'14px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontWeight:700,fontSize:15,color:T.text}}>All Employees — {MONTHS[month-1]} {year}</span>
          <span style={{fontWeight:400,fontSize:12,color:T.textSm}}>{summary.length} employees</span>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px 80px 80px 90px 70px',padding:'10px 20px',background:'#faf9ff',borderBottom:`1px solid ${T.borderL}`}}>
          {['Employee','Present','Absent','Leave','Perm','Holiday','Attend %',''].map(h=>(
            <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>
          ))}
        </div>
        {loading?<Spinner/>:summary.length===0?(
          <div style={{padding:28,textAlign:'center',color:T.textSm,fontSize:14}}>No data for this month</div>
        ):(
          summary.map((emp,idx)=>{
            const working=emp.present+emp.absent+emp.leave+emp.permission+emp.holiday;
            const pct=fmtPct(emp.present,working);
            const pctColor=pct>=90?T.green:pct>=75?T.purple:pct>=50?T.yellow:T.red;
            return(
              <div key={emp.id} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px 80px 80px 90px 70px',padding:'12px 20px',borderBottom:idx<summary.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center',background:selectedUser===emp.id?'#faf5ff':'transparent',cursor:'pointer',transition:'background 0.15s'}} onClick={()=>loadUserDetail(emp.id)}>
                <div style={{display:'flex',alignItems:'center',gap:9}}>
                  <div style={{width:30,height:30,borderRadius:8,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,color:T.purpleText,flexShrink:0}}>
                    {emp.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13,color:T.text}}>{emp.name}</div>
                    <div style={{fontWeight:400,fontSize:11,color:T.textSm,textTransform:'capitalize'}}>{emp.role}</div>
                  </div>
                </div>
                <span style={{fontWeight:600,fontSize:13,color:T.green}}>{emp.present}</span>
                <span style={{fontWeight:600,fontSize:13,color:emp.absent>0?T.red:T.textSm}}>{emp.absent}</span>
                <span style={{fontWeight:600,fontSize:13,color:T.purple}}>{emp.leave}</span>
                <span style={{fontWeight:600,fontSize:13,color:'#db2777'}}>{emp.permission}</span>
                <span style={{fontWeight:600,fontSize:13,color:T.yellow}}>{emp.holiday}</span>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <div style={{flex:1,height:6,background:'#f0edfb',borderRadius:10,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pct}%`,background:pctColor,borderRadius:10}}/>
                  </div>
                  <span style={{fontWeight:700,fontSize:12,color:pctColor,minWidth:32}}>{pct}%</span>
                </div>
                <span style={{fontWeight:500,fontSize:11,color:T.purple}}>View →</span>
              </div>
            );
          })
        )}
      </div>

      {/* Individual employee detail */}
      {selectedUser&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:'20px',boxShadow:T.shadow}}>
          <div style={{fontWeight:700,fontSize:15,color:T.text,marginBottom:14}}>
            {summary.find(u=>u.id===selectedUser)?.name} — Day-by-Day Detail
          </div>
          {detailLoading?<Spinner/>:(
            <div style={{maxHeight:380,overflow:'auto'}}>
              <div style={{display:'grid',gridTemplateColumns:'110px 110px 110px 90px 100px',padding:'10px 16px',background:'#faf9ff',borderRadius:9,marginBottom:6}}>
                {['Date','Login','Logout','Duration','Status'].map(h=>(
                  <span key={h} style={{fontWeight:600,fontSize:11,color:T.textSm,textTransform:'uppercase',letterSpacing:0.7}}>{h}</span>
                ))}
              </div>
              {(userDetail||[]).filter(r=>r.status!=='weekend').map((rec,idx,arr)=>{
                const cfg={
                  present:{bg:T.greenBg,c:T.green},absent:{bg:T.redBg,c:T.red},
                  leave:{bg:T.purpleBg,c:T.purple},permission:{bg:'#fce7f3',c:'#db2777'},
                  holiday:{bg:T.yellowBg,c:T.yellow},regularized:{bg:T.blueBg,c:T.blue},
                }[rec.status]||{bg:'#f3f4f6',c:'#6b7280'};
                const isHalfDay=rec.is_half_day&&rec.status==='present';
                return(
                  <div key={rec.id||idx} style={{display:'grid',gridTemplateColumns:'110px 110px 110px 90px 100px',padding:'10px 16px',borderBottom:idx<arr.length-1?`1px solid ${T.borderL}`:'none',alignItems:'center'}}>
                    <span style={{fontWeight:500,fontSize:13,color:T.textMd}}>{rec.date}</span>
                    <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{rec.login_time?new Date(rec.login_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}):'—'}</span>
                    <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{rec.logout_time?new Date(rec.logout_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}):'—'}</span>
                    <span style={{fontWeight:400,fontSize:13,color:T.textMd}}>{rec.duration_minutes?`${Math.floor(rec.duration_minutes/60)}h ${String(rec.duration_minutes%60).padStart(2,'0')}m`:'—'}</span>
                    <span style={{display:'inline-flex',alignItems:'center',gap:4,background:isHalfDay?'#ccfbf1':cfg.bg,color:isHalfDay?'#0d9488':cfg.c,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:20}}>
                      {isHalfDay?'Half Day':rec.status.charAt(0).toUpperCase()+rec.status.slice(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN ADMIN ATTENDANCE PAGE
══════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════
   WORK REPORTS SECTION — Editor output + project tracking
══════════════════════════════════════════════════ */
function WorkReportsSection({users}) {
  const editors = users.filter(u=>u.role==='editor');
  const [month,setMonth]=useState(new Date().getMonth()+1);
  const [year,setYear]=useState(new Date().getFullYear());
  const [reports,setReports]=useState({});
  const [loading,setLoading]=useState(false);
  const [selectedEditor,setSelectedEditor]=useState(null);
  const [overview,setOverview]=useState(null);

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const ov=await api.get('/tasks/reports/overview');
      setOverview(ov.data);
      const results={};
      await Promise.all(editors.map(async e=>{
        try{
          const r=await api.get(`/tasks/reports/editor/${e.id}`,{params:{month,year}});
          results[e.id]=r.data;
        }catch{}
      }));
      setReports(results);
    }catch{toast.error('Failed to load work reports');}
    finally{setLoading(false);}
  },[month,year]);

  useEffect(()=>{if(editors.length>0)load();},[month,year]);

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <h2 style={{fontWeight:800,fontSize:22,color:T.text}}>Work Reports</h2>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
            <button onClick={()=>{if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={15}/></button>
            <span style={{fontWeight:600,fontSize:13,color:T.text,minWidth:105,textAlign:'center'}}>{MONTHS[month-1]} {year}</span>
            <button onClick={()=>{if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={15}/></button>
          </div>
          <Btn variant="secondary" onClick={load} small><RefreshCw size={13}/>Refresh</Btn>
        </div>
      </div>

      {/* Overall task overview */}
      {overview&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:14,marginBottom:24}}>
          {[
            {l:'Total Tasks',v:overview.total||0,bg:T.purpleBg,c:T.purple},
            {l:'Completed',v:(overview.by_status?.client_approved||0)+(overview.by_status?.uploaded||0),bg:T.greenBg,c:T.green},
            {l:'In Review',v:overview.by_status?.review||0,bg:T.blueBg,c:T.blue},
            {l:'Rework',v:(overview.by_status?.rework||0)+(overview.by_status?.client_rework||0),bg:T.redBg,c:T.red},
            {l:'Overdue',v:overview.overdue||0,bg:'#ffedd5',c:'#ea580c'},
            {l:'Pending Approval',v:overview.by_status?.client_approval||0,bg:T.yellowBg,c:T.yellow},
          ].map(({l,v,bg,c})=>(
            <div key={l} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'14px 16px',boxShadow:T.shadow}}>
              <div style={{fontWeight:800,fontSize:22,color:c,lineHeight:1}}>{v}</div>
              <div style={{fontWeight:500,fontSize:12,color:T.textSm,marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Editor workload cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16,marginBottom:24}}>
        {editors.map(editor=>{
          const rep=reports[editor.id];
          const ov=overview?.editor_workload?.find(e=>e.editor.id===editor.id);
          return(
            <div key={editor.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow,cursor:'pointer'}}
              onClick={()=>setSelectedEditor(selectedEditor?.id===editor.id?null:editor)}>
              <div style={{background:selectedEditor?.id===editor.id?T.purple:'#1a1035',padding:'14px 18px',display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:38,height:38,borderRadius:10,background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:15,color:'#fff',flexShrink:0}}>
                  {editor.name[0].toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#fff'}}>{editor.name}</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.55)'}}>Editor</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',borderBottom:`1px solid ${T.borderL}`}}>
                {[
                  {l:'Total',v:ov?.total||0,c:T.text},
                  {l:'Done',v:ov?.completed||0,c:T.green},
                  {l:'Pending',v:ov?.pending||0,c:T.yellow},
                  {l:'Rework',v:ov?.rework||0,c:T.red},
                ].map(({l,v,c})=>(
                  <div key={l} style={{padding:'12px 14px',borderRight:`1px solid ${T.borderL}`,textAlign:'center'}}>
                    <div style={{fontWeight:800,fontSize:18,color:c}}>{v}</div>
                    <div style={{fontSize:10,color:T.textXs,marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>
              {rep&&(
                <div style={{padding:'10px 18px',display:'flex',gap:16,background:'#faf9ff'}}>
                  <div><div style={{fontSize:10,color:T.textXs}}>This Month Completed</div><div style={{fontSize:14,fontWeight:700,color:T.green}}>{rep.completed}</div></div>
                  <div><div style={{fontSize:10,color:T.textXs}}>Reworks</div><div style={{fontSize:14,fontWeight:700,color:T.red}}>{rep.rework_count}</div></div>
                  <div><div style={{fontSize:10,color:T.textXs}}>Overdue</div><div style={{fontSize:14,fontWeight:700,color:'#ea580c'}}>{rep.overdue}</div></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected editor detail */}
      {selectedEditor&&reports[selectedEditor.id]&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          <div style={{padding:'14px 20px',borderBottom:`1px solid ${T.borderL}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{fontWeight:700,fontSize:15,color:T.text}}>{selectedEditor.name} — {MONTHS[month-1]} {year}</div>
            <button onClick={()=>setSelectedEditor(null)} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm}}><X size={16}/></button>
          </div>
          {reports[selectedEditor.id].completed_tasks?.length>0&&(
            <div style={{padding:'14px 20px',borderBottom:`1px solid ${T.borderL}`}}>
              <div style={{fontWeight:600,fontSize:13,color:T.green,marginBottom:10}}>✅ Completed ({reports[selectedEditor.id].completed_tasks.length}) — Rework NOT counted here</div>
              {reports[selectedEditor.id].completed_tasks.map((t,idx)=>(
                <div key={t.id} style={{display:'flex',alignItems:'center',gap:12,padding:'7px 0',borderBottom:idx<reports[selectedEditor.id].completed_tasks.length-1?`1px solid ${T.borderL}`:'none'}}>
                  <span style={{fontSize:11,color:T.textXs,minWidth:65}}>{t.task_code}</span>
                  <span style={{fontSize:13,color:T.text,fontWeight:500,flex:1}}>{t.content_description}</span>
                  <span style={{fontSize:11,color:T.textSm}}>{t.client_name}</span>
                  {t.rework_count>0&&<span style={{background:T.redBg,color:T.red,fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:10}}>Rework ×{t.rework_count}</span>}
                </div>
              ))}
            </div>
          )}
          {reports[selectedEditor.id].pending_tasks?.length>0&&(
            <div style={{padding:'14px 20px'}}>
              <div style={{fontWeight:600,fontSize:13,color:T.yellow,marginBottom:10}}>⏳ Pending ({reports[selectedEditor.id].pending_tasks.length})</div>
              {reports[selectedEditor.id].pending_tasks.map((t,idx)=>(
                <div key={t.id} style={{display:'flex',alignItems:'center',gap:12,padding:'7px 0',borderBottom:idx<reports[selectedEditor.id].pending_tasks.length-1?`1px solid ${T.borderL}`:'none'}}>
                  <span style={{fontSize:11,color:T.textXs,minWidth:65}}>{t.task_code}</span>
                  <span style={{fontSize:13,color:T.text,fontWeight:500,flex:1}}>{t.content_description}</span>
                  <span style={{fontSize:11,color:T.textSm}}>{t.client_name}</span>
                  {t.due_date&&<span style={{fontSize:11,color:new Date(t.due_date+'T00:00:00')<new Date()?T.red:T.textSm,fontWeight:600}}>{t.due_date}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading&&<Spinner/>}
    </div>
  );
}

export default function AdminAttendancePage() {
  const [activeSection,setActiveSection]=useState('today');
  const [users,setUsers]=useState([]);
  const [pendingCount,setPendingCount]=useState(0);

  useEffect(()=>{
    // Load employees only (no admin)
    api.get('/auth/users').then(r=>{
      setUsers(r.data.filter(u=>u.role!=='admin'));
    }).catch(()=>{});
    // Load pending count for badge
    api.get('/leaves/admin/pending-count').then(r=>setPendingCount(r.data.total||0)).catch(()=>{});
  },[]);

  const NAV=[
    {id:'today',l:'Today Overview',I:CheckCircle},
    {id:'staff',l:'All Staff',I:Users},
    {id:'approvals',l:'Approvals',I:Bell,badge:pendingCount},
    {id:'holidays',l:'Holidays',I:Calendar},
    {id:'salary',l:'Salary',I:DollarSign},
    {id:'reports',l:'Reports',I:BarChart2},
    {id:'work',l:'Work Reports',I:TrendingUp},
  ];

  return(
    <div style={{fontFamily:T.font}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Page header */}
      <div style={{marginBottom:24}}>
        <h1 style={{fontWeight:800,fontSize:26,color:T.text,letterSpacing:-0.5,marginBottom:4}}>Attendance Management</h1>
        <p style={{fontWeight:400,fontSize:15,color:T.textSm}}>Full control over employee attendance, leaves and salary</p>
      </div>

      {/* Sub-navigation */}
      <div style={{display:'flex',gap:8,marginBottom:28,background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'6px',width:'fit-content',boxShadow:T.shadow,flexWrap:'wrap'}}>
        {NAV.map(({id,l,I,badge})=>(
          <button key={id} onClick={()=>setActiveSection(id)}
            style={{display:'flex',alignItems:'center',gap:7,background:activeSection===id?T.purpleBg:'transparent',border:activeSection===id?`1px solid #c4b5fd`:'1px solid transparent',borderRadius:9,padding:'9px 16px',fontSize:13,fontWeight:activeSection===id?700:500,color:activeSection===id?T.purpleText:T.textMd,cursor:'pointer',fontFamily:T.font,position:'relative',transition:'all 0.15s'}}>
            <I size={15} color={activeSection===id?T.purple:T.textSm}/>
            {l}
            {badge>0&&(
              <span style={{position:'absolute',top:4,right:4,background:T.red,color:'#fff',fontSize:9,fontWeight:700,minWidth:16,height:16,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection==='today'&&<TodaySection/>}
      {activeSection==='staff'&&<AllStaffSection users={users}/>}
      {activeSection==='approvals'&&<ApprovalsSection/>}
      {activeSection==='holidays'&&<HolidaySection/>}
      {activeSection==='salary'&&<SalarySection users={users}/>}
      {activeSection==='reports'&&<ReportsSection users={users}/>}
      {activeSection==='work'&&<WorkReportsSection users={users}/>}
    </div>
  );
}